import React from 'react';
import { useTriggers, executionModes } from '../../../../Data/Journal';
import { dateToString } from '../../../../Services/helpers';
import { useDialogues } from '../../../../Components/Dialogues';
import Icon from '../../../../Components/Icons/Icon';
import Tile from '../../../../Components/Containers/Tile';
import Time from '../../../../Components/Formatting/Time';

const TriggersWidget = () => {
    const today = dateToString(new Date());
    const { triggers, executeTrigger } = useTriggers(true, today);
    const { confirm, info } = useDialogues();

    if (!triggers) {
        return null;
    }

    const manualTriggers = triggers.filter(
        t => t.executionMode === executionModes.MANUAL || t.executionMode === executionModes.SCHEDULED
    );

    if (manualTriggers.length === 0) {
        return null;
    }

    const handleExecute = async (trigger) => {
        const available = trigger.availableCount ?? 0;

        if (available === 0) {
            info({
                theme: 'primary',
                title: 'No available notifications',
                message: `All notifications for "${trigger.type}" have already been sent today.`,
            });
            return;
        }

        const isScheduled = trigger.executionMode === executionModes.SCHEDULED && trigger.schedule;
        const message = isScheduled
            ? <ExecuteScheduledMessage {...{available, schedule: trigger.schedule}} />
            : `This will send ${available} notification${available === 1 ? '' : 's'} to customers.`;

        confirm(
            {
                theme: 'primary',
                title: `Execute ${trigger.triggerType}?`,
                message,
                confirmLabel: 'Execute',
            },
            () => executeTrigger(trigger.id, today)
        );
    };

    return (
        <Tile {...{ title: "Notifications", icon: "Bell", flush: true }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {manualTriggers.map(trigger => (
                    <TriggerBox
                        key={trigger.id}
                        {...{ trigger, onExecute: () => handleExecute(trigger) }}
                    />
                ))}
            </div>
        </Tile>
    );
};

const ExecuteScheduledMessage = ({ available, schedule }) => {
    return <>
        This trigger is scheduled to be sent shortly after <Time time={schedule} />, but you can send {available} notification{available === 1 ? '' : 's'} now to customers.
    </>;
};

const TriggerBox = ({ trigger, onExecute }) => {
    const executedCount = trigger.executedCount ?? 0;
    const availableCount = trigger.availableCount ?? 0;
    const isScheduled = trigger.executionMode === executionModes.SCHEDULED && trigger.schedule;

    return (
        <button
            onClick={onExecute}
            className="surface-1 rounded-lg surface-outline p-4 flex flex-col gap-2 text-left"
        >
            <div className="flex flex-row items-start justify-between gap-2">
                <div className="flex-1">
                    <h3 className="font-bold text-sm">{trigger.type}</h3>
                    {isScheduled && (
                        <div className="flex flex-row items-center gap-2 mt-1">
                            <Icon {...{ icon: "Clock", size: "1x", className: "text-muted" }} />
                            <span className="text-xs text-muted">
                                Scheduled at <Time time={trigger.schedule} />
                            </span>
                        </div>
                    )}
                    <div className="flex flex-row items-center gap-2 mt-2">
                        <Icon {...{ icon: "PaperPlane", size: "1x", className: "text-brand" }} />
                        <span className="text-sm text-muted">
                            {executedCount} sent / {availableCount} available
                        </span>
                    </div>
                </div>
                <div className="shrink-0">
                    {availableCount > 0 ? (
                        <div className="bg-brand text-white rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold">
                            {availableCount}
                        </div>
                    ) : (
                        <Icon {...{ icon: "Check", size: "1.5x", className: "text-green-600" }} />
                    )}
                </div>
            </div>
        </button>
    );
};

export default TriggersWidget;
